import { useEffect, useState, useMemo } from 'react'
import { Button, Card, Container, FormCheck, Row, Table } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import KButton from '../components/KButton'
import KConfirm from '../components/KConfirm'
import KLayerNavigation from '../components/KLayerNavigation'
import SubscriptionRequiredNotice from '../components/SubscriptionRequiredNotice'
import {
  apiRequest,
  getUserAccess,
  hideKAlert,
  isPiiEncryptionEnabled,
  isUserLoggedIn,
  showKAlert,
} from '../helpers'
import { t } from '../locale'
import { User, UserAccess, UserFilter } from '../react-app-env'
import { fetchUsers, deleteUserById, resendUserConfirmation } from '../service/user'
import UserToolbar from './components/UserToolbar'

function Users() {
  const userAccess: UserAccess = getUserAccess()
  const navigate = useNavigate()

  const [searchKeyword, setSearchKeyword] = useState('')
  const [filterConfirmed, setFilterConfirmed] = useState('')
  const [filterAdmin, setFilterAdmin] = useState('')
  const [debouncedKeyword, setDebouncedKeyword] = useState('')
  const [newUser, setNewUser] = useState(true)
  const [subscriptionChecked, setSubscriptionChecked] = useState(false)
  const [table, setTable] = useState<boolean>(false)
  const [searchParams] = useSearchParams()
  const activeParam = searchParams.get('active')
  const [sortKey, setSortKey] = useState<'lastName' | 'firstName' | 'email'>('lastName')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const defaultFilter: UserFilter = {
    gender: undefined,
    keyword: '',
    active: activeParam === 'false' ? false : true,
    confirmed: undefined,
    admin: undefined,
    sortBy: 'lastName',
    sortDirection: 1,
  }

  const [filter, setFilter] = useState(defaultFilter)

  const [users, setUsers] = useState([] as Array<User>)
  const [total, setTotal] = useState(0)
  const [currentUser, setCurrentUser] = useState({} as User)
  const [action, setAction] = useState('')
  const [confirmShow, setConfirmShow] = useState(false)

  // Adding a team member beyond the one included in the subscription bills a
  // one-time fee to the company's default card automatically - the admin
  // doesn't choose a method, we just warn them and confirm a card is on file.

  async function deleteUser() {
    if (!currentUser?._id) return

    const response = await deleteUserById(currentUser._id)

    if (response?.ok) {
      showKAlert('success', 'userDeleted')
      const _users = users.filter(u => u._id !== currentUser._id)
      setUsers(_users)
      setTotal(_users.length)
    }
  }

  async function resendConfirmationEmail() {
    if (!currentUser?._id) return

    const response = await resendUserConfirmation(currentUser._id)

    if (response?.ok) {
      showKAlert('success', 'Confirmation Request Sent')
    }
  }

  function doAction(user_id?: string) {
    switch (action) {
      case 'delete':
        deleteUser()
        break
      case 'resendConfirmationEmail':
        resendConfirmationEmail()
        break
      default:
        break
    }

    setConfirmShow(false)
  }

  useEffect(() => {
    checkSubscription()
    const loadUsers = async () => {
      const response = await fetchUsers(filter)

      if (response?.users) {
        setUsers(response.users)
        setTotal(response.users.length)
      }
    }

    loadUsers()
  }, [filter])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedKeyword(searchKeyword)
    }, 300)

    return () => clearTimeout(handler)
  }, [searchKeyword])

  // Ažurira filter samo ako se keyword zaista promenio
  useEffect(() => {
    setFilter(prevFilter => {
      if (prevFilter?.keyword === debouncedKeyword) return prevFilter
      return { ...prevFilter, keyword: debouncedKeyword }
    })
  }, [debouncedKeyword])

  useEffect(() => {
    setFilter(prev => ({
      ...prev,
      active: activeParam === 'false' ? false : true,
    }))
  }, [activeParam])

  function handleFilterConfirmed(value: string) {
    setFilterConfirmed(value)
    setFilter(prev => ({ ...prev, confirmed: value !== '' ? value === 'true' : undefined }))
  }
  function handleFilterAdmin(value: string) {
    setFilterAdmin(value)
    setFilter(prev => ({ ...prev, admin: value !== '' ? value === 'true' : undefined }))
  }
  function handleOrderBy(e: any) {
    const key = e.target.id

    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  function showAll() {
    setSearchKeyword('')
    setFilterConfirmed('')
    setFilterAdmin('')
    setFilter(defaultFilter)
  }

  // Adding a team member beyond the one included in the subscription bills a
  // one-time fee against the company's saved card, which requires an active
  // paid subscription (not just a trial) - see chargeAdditionalSeat.
  const checkSubscription = async () => {
    try {
      const res = await apiRequest(`${process.env.REACT_APP_API_URL}/subscription/check`, 'POST', {
        subdomain: window.subdomain,
      })

      setNewUser(!!res.status && res.type === 'subscribed')
      setSubscriptionChecked(true)
    } catch (error) {
      console.error('Error checking subscription:', error)
    }
  }

  const sortedUsers = useMemo(() => {
    const arr = [...users]

    arr.sort((a, b) => {
      let va: any = a[sortKey] ?? ''
      let vb: any = b[sortKey] ?? ''

      if (typeof va === 'string') va = va.toLowerCase()
      if (typeof vb === 'string') vb = vb.toLowerCase()

      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1

      return 0
    })

    return arr
  }, [users, sortKey, sortDir])

  const addToDsSignTeam = async (userId: string | undefined) => {
    try {
      await apiRequest(`${process.env.REACT_APP_API_URL}/sign/user/sync`, 'POST', { userId })
    } catch (error) {
      console.error('Error adding user to DS|Sign team:', error)
      showKAlert('danger', 'Failed to add user to DS|Sign team')
    }
  }
  return (
    <>
      <Container>
        <Row>
          <div className='sidebar col-md-3 col-lg-2 p-0'>
            <KLayerNavigation logged={isUserLoggedIn()} />
          </div>
          <main className='col-md-9 ms-sm-auto col-lg-10 px-md-4'>
            <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3'>
              <h1 className='h2'>{activeParam === 'false' ? t('inactiveUsers') : t('users')}</h1>

              {newUser && (
                <div className='btn-toolbar mb-2 mb-md-0'>
                  <Button
                    variant='none'
                    className='btn-sm btn-outline-primary rounded-pill'
                    onClick={() => navigate('/user/create')}
                  >
                    <i className='bi bi-plus-circle'></i>
                    {t('newUser')}
                  </Button>
                </div>
              )}
            </div>

            {subscriptionChecked && !newUser && (
              <SubscriptionRequiredNotice message='trialAddUserRequiresSubscription' />
            )}

            <UserToolbar
              searchKeyword={searchKeyword}
              onSearchChange={setSearchKeyword}
              onShowAll={showAll}
              filterAdmin={filterAdmin}
              onFilterAdmin={handleFilterAdmin}
              filterConfirmed={filterConfirmed}
              onFilterConfirmed={handleFilterConfirmed}
              sortKey={sortKey}
              sortDir={sortDir}
              onSortChange={(key, dir) => {
                setSortKey(key)
                setSortDir(dir)
              }}
              onViewChange={setTable}
            />
            {!table && (
              <div className='table-responsive small'>
                <Table className='table-striped table-sm'>
                  <thead>
                    <tr>
                      <th scope='col'>{t('gender')}</th>
                      <th id='lastName' scope='col' onClick={handleOrderBy}>
                        {t('lastName')}
                      </th>
                      <th id='firstName' scope='col' onClick={handleOrderBy}>
                        {t('firstName')}
                      </th>
                      <th scope='col'>{t('email')}</th>
                      <th scope='col'>{t('phone')}</th>
                      <th scope='col'>{t('active')}</th>
                      <th scope='col'>{t('confirmed')}</th>
                      <th scope='col'>{t('admin')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.map(user => (
                      <tr key={user._id} id={user._id}>
                        <td>{t(user.gender)}</td>
                        <td>{user.lastName}</td>
                        <td>{user.firstName}</td>
                        <td>{user.email}</td>
                        <td>{user.phone}</td>
                        <td className='text-center'>
                          <FormCheck
                            name='active[]'
                            checked={user.active}
                            onChange={(e: any) => e.preventDefault()}
                            onClick={(e: any) => e.preventDefault()}
                          />
                        </td>
                        <td className='text-center'>
                          <FormCheck
                            name='confirmed[]'
                            checked={user.confirmed}
                            onChange={(e: any) => e.preventDefault()}
                            onClick={(e: any) => e.preventDefault()}
                          />
                        </td>
                        <td className='text-center'>
                          <FormCheck
                            name='admin[]'
                            checked={user.permissions.admin}
                            onChange={(e: any) => e.preventDefault()}
                            onClick={(e: any) => e.preventDefault()}
                          />
                        </td>
                        <td className='d-flex flex-nowrap gap-1'>
                          <KButton
                            className='p-1 text-white'
                            size='sm'
                            icon='eye'
                            variant='blue'
                            title={t('show')}
                            onClick={() => {
                              hideKAlert()
                              navigate(`/user/${user._id}/show`)
                            }}
                          />
                          <KButton
                            className='p-1 text-white'
                            size='sm'
                            variant='warning'
                            title={t('edit')}
                            icon='pencil-square'
                            onClick={() => {
                              hideKAlert()
                              navigate(`/user/${user._id}/edit`)
                            }}
                          />
                          {!isPiiEncryptionEnabled() && userAccess.permissions.main && (
                            <KButton
                              variant='primary'
                              id='user-ds-sign-integration'
                              className='btn-user-edit'
                              icon='pencil-fill'
                              title={
                                user.dsSignUser ? t('userAddedForDsSign') : t('dsSignIntegration')
                              }
                              disabled={user.dsSignUser}
                              onClick={() => {
                                addToDsSignTeam(user._id)
                              }}
                            />
                          )}
                          {!user.permissions.main && (
                            <KButton
                              variant='danger'
                              className='btn-user-delete'
                              icon='trash'
                              title={t('deleteUser')}
                              onClick={() => {
                                setCurrentUser(user)
                                setAction('delete')
                                setConfirmShow(true)
                              }}
                            />
                          )}
                          {!user.permissions.main && !user.confirmed && (
                            <KButton
                              variant='secondary'
                              className='btn-user-resend-confirmation-email'
                              icon='envelope-fill'
                              title={t('resendConfirmationEmail')}
                              onClick={() => {
                                setCurrentUser(user)
                                setAction('resendConfirmationEmail')
                                setConfirmShow(true)
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div>
                  {t('total')}: <b>{total}</b>
                </div>
              </div>
            )}
            {table && (
              <Row className='g-3 mb-4'>
                {users.length > 0 ? (
                  sortedUsers.map(user => (
                    <div className='col-sm-4 mb-3 mb-sm-0' key={user._id}>
                      <Card className='border-3 shadow'>
                        <Card.Body>
                          {/* Name */}
                          <h5
                            className='card-title mb-2 text-truncate'
                            title={`${user.firstName} ${user.lastName}`}
                          >
                            {user.firstName} {user.lastName}
                          </h5>

                          {/* Email */}
                          <p className='card-text mb-1'>
                            <strong>Email:</strong> {user.email}
                          </p>

                          {/* Phone */}
                          <p className='card-text mb-1'>
                            <strong>Phone number:</strong> {user.phone || '-'}
                          </p>

                          {/* Admin */}
                          <p className='card-text mb-3'>
                            <strong>Admin:</strong> {user.permissions.admin ? t('yes') : t('no')}
                          </p>

                          {/* Buttons */}
                          <div className='d-flex flex-wrap gap-2'>
                            <KButton
                              className='p-1 text-white'
                              size='sm'
                              icon='eye'
                              variant='blue'
                              title={t('show')}
                              onClick={() => {
                                hideKAlert()
                                navigate(`/user/${user._id}/show`)
                              }}
                            />
                            <KButton
                              className='p-1 text-white'
                              size='sm'
                              variant='warning'
                              title={t('edit')}
                              icon='pencil-square'
                              onClick={() => {
                                hideKAlert()
                                navigate(`/user/${user._id}/edit`)
                              }}
                            />

                            {!isPiiEncryptionEnabled() && userAccess.permissions.main && (
                              <KButton
                                variant='primary'
                                id='user-ds-sign-integration'
                                className='btn-user-edit'
                                icon='pencil-fill'
                                title={
                                  user.dsSignUser ? t('userAddedForDsSign') : t('dsSignIntegration')
                                }
                                disabled={user.dsSignUser}
                                onClick={() => {
                                  addToDsSignTeam(user._id)
                                }}
                              />
                            )}

                            {!user.permissions.main && (
                              <KButton
                                variant='danger'
                                className='btn-user-delete'
                                icon='trash'
                                title={t('deleteUser')}
                                onClick={() => {
                                  setCurrentUser(user)
                                  setAction('delete')
                                  setConfirmShow(true)
                                }}
                              />
                            )}

                            {!user.permissions.main && !user.confirmed && (
                              <KButton
                                variant='secondary'
                                className='btn-user-resend-confirmation-email'
                                icon='envelope-fill'
                                title={t('resendConfirmationEmail')}
                                onClick={() => {
                                  setCurrentUser(user)
                                  setAction('resendConfirmationEmail')
                                  setConfirmShow(true)
                                }}
                              />
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  ))
                ) : (
                  <div className='col-12 text-center'>{t('noUsersFound')}</div>
                )}
              </Row>
            )}
          </main>
        </Row>
      </Container>
      <KConfirm
        show={confirmShow}
        onConfirm={() => {
          doAction()
        }}
        onCancel={() => {
          setConfirmShow(false)
        }}
      />
    </>
  )
}

export default Users
